import { useState, useCallback, useRef, useEffect } from 'react'
import type { Camera, CameraCapabilities } from '../../types'
import { IntelligenceType } from '../../types'
import { useToast } from '../ui/Toast'
import {
  ptzRotate, ptzStop, ptzZoom, ptzAutoFocus,
  ptzGoHome, ptzSetHome,
  ptzListPresets, ptzInvokePreset, ptzAddPreset, ptzDeletePreset,
  wiperOpen, wiperClose, washOpen,
  setIrLight, setWhiteLight, setDayNightMode,
  downloadSnapshot,
  probeCapabilities,
} from '../../lib/cameraApi'

interface Props {
  camera: Camera
  onClose: () => void
  onOpenSettings?: () => void
}

interface Preset {
  id: number
  name: string
}

export function PtzPanel({ camera, onClose, onOpenSettings }: Props) {
  const { toast } = useToast()
  const [speed, setSpeed] = useState(32)
  const [irOn, setIrOn] = useState(false)
  const [whiteOn, setWhiteOn] = useState(false)
  const [wiperOn, setWiperOn] = useState(false)
  const [dayNight, setDayNight] = useState<0 | 1 | 2>(0)
  const [presets, setPresets] = useState<Preset[]>([])
  const [newPresetName, setNewPresetName] = useState('')
  const [showPresets, setShowPresets] = useState(false)
  const [caps, setCaps] = useState<CameraCapabilities | null>(null)
  const [probing, setProbing] = useState(true)
  const movingRef = useRef(false)

  const cmd = useCallback(async (fn: () => Promise<unknown>, label: string) => {
    try {
      await fn()
    } catch {
      toast(`${label} failed`, 'error')
    }
  }, [toast])

  // ── Probe capabilities on mount ──

  useEffect(() => {
    let cancelled = false
    setProbing(true)
    probeCapabilities(camera.id).then(c => {
      if (!cancelled) {
        setCaps(c)
        setProbing(false)
      }
    }).catch(() => {
      if (!cancelled) {
        // If probe fails, show all controls as fallback
        setCaps({ ptz: true, presetMaxNum: 16, intelligences: [] })
        setProbing(false)
      }
    })
    return () => { cancelled = true }
  }, [camera.id])

  // ── PTZ D-pad ──

  const startMove = useCallback((pan: number, tilt: number) => {
    movingRef.current = true
    cmd(() => ptzRotate(camera.id, pan, tilt), 'PTZ move')
  }, [camera.id, cmd])

  const stopMove = useCallback(() => {
    if (movingRef.current) {
      movingRef.current = false
      ptzStop(camera.id)
    }
  }, [camera.id])

  // Stop on unmount
  useEffect(() => {
    return () => { ptzStop(camera.id) }
  }, [camera.id])

  // ── Presets ──

  const loadPresets = useCallback(async () => {
    try {
      const result = await ptzListPresets(camera.id)
      if (result.ok && result.body) {
        const parsed: Preset[] = []
        const lines = result.body.split('\n')
        for (const line of lines) {
          const idMatch = line.match(/presetID=(\d+)/)
          const nameMatch = line.match(/presetName=(.+)/)
          if (idMatch) {
            parsed.push({
              id: parseInt(idMatch[1]),
              name: nameMatch ? nameMatch[1].trim() : `Preset ${idMatch[1]}`,
            })
          }
        }
        setPresets(parsed)
      }
    } catch {
      // ignore
    }
  }, [camera.id])

  useEffect(() => {
    if (caps?.ptz) loadPresets()
  }, [loadPresets, caps?.ptz])

  const handleAddPreset = useCallback(async () => {
    if (!newPresetName.trim()) return
    const nextId = presets.length > 0 ? Math.max(...presets.map(p => p.id)) + 1 : 1
    await cmd(() => ptzAddPreset(camera.id, nextId, newPresetName.trim()), 'Add preset')
    setNewPresetName('')
    loadPresets()
  }, [camera.id, newPresetName, presets, cmd, loadPresets])

  // ── Derived capability flags ──
  const hasPtz = caps?.ptz ?? true
  const hasIntelligences = (caps?.intelligences.length ?? 0) > 0
  const hasAnyDetection = hasIntelligences || true // always show motion detection as it's basic

  // ── Button styles ──

  const btnBase = 'text-xs px-2.5 py-1.5 rounded transition-colors font-medium'
  const btnDefault = `${btnBase} bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200`
  const btnActive = `${btnBase} bg-brand text-white`
  const dpadBtn = 'w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors text-sm select-none'

  return (
    <div className="fixed top-0 right-0 bottom-0 w-72 bg-sidebar border-l border-card-border overflow-y-auto z-10">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-card-border">
        <div>
          <p className="text-sm font-medium">{camera.name}</p>
          <p className="text-xs text-gray-500">
            {caps?.deviceModel ? `${caps.deviceModel} · ` : ''}{camera.cam_ip}:{camera.port}
          </p>
          {caps?.firmwareVersion && (
            <p className="text-[10px] text-gray-400">FW {caps.firmwareVersion}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700 text-lg leading-none transition-colors"
        >
          &times;
        </button>
      </div>

      {probing ? (
        <div className="p-4 text-center text-xs text-gray-500">Detecting camera capabilities...</div>
      ) : (
        <div className="p-3 space-y-4">
          {/* ── Detection / Settings shortcut ── */}
          {hasAnyDetection && onOpenSettings && (
            <button
              onClick={onOpenSettings}
              className="w-full bg-brand/10 hover:bg-brand/20 text-brand text-xs font-medium py-2.5 rounded transition-colors flex items-center justify-center gap-2"
            >
              <span>&#9881;</span>
              Camera Settings & Detection Zones
            </button>
          )}

          {/* ── D-Pad ── */}
          {hasPtz && (
            <Section title="Pan / Tilt">
              <div className="flex flex-col items-center gap-1">
                <button
                  className={dpadBtn}
                  onPointerDown={() => startMove(0, speed)}
                  onPointerUp={stopMove}
                  onPointerLeave={stopMove}
                >&#9650;</button>

                <div className="flex gap-1">
                  <button
                    className={dpadBtn}
                    onPointerDown={() => startMove(-speed, 0)}
                    onPointerUp={stopMove}
                    onPointerLeave={stopMove}
                  >&#9664;</button>

                  <button
                    className={`${dpadBtn} text-[10px]`}
                    onClick={() => cmd(() => ptzGoHome(camera.id), 'Go home')}
                    title="Go to home position"
                  >HOME</button>

                  <button
                    className={dpadBtn}
                    onPointerDown={() => startMove(speed, 0)}
                    onPointerUp={stopMove}
                    onPointerLeave={stopMove}
                  >&#9654;</button>
                </div>

                <button
                  className={dpadBtn}
                  onPointerDown={() => startMove(0, -speed)}
                  onPointerUp={stopMove}
                  onPointerLeave={stopMove}
                >&#9660;</button>
              </div>

              <div className="flex items-center gap-2 mt-2">
                <span className="text-[10px] text-gray-500 w-8">Speed</span>
                <input
                  type="range"
                  min={1}
                  max={63}
                  value={speed}
                  onChange={(e) => setSpeed(Number(e.target.value))}
                  className="flex-1 accent-brand h-1"
                />
                <span className="text-[10px] text-gray-500 w-5 text-right">{speed}</span>
              </div>
            </Section>
          )}

          {/* ── Zoom ── */}
          <Section title="Zoom / Focus">
            <div className="flex gap-1.5">
              <button
                className={btnDefault}
                onPointerDown={() => cmd(() => ptzZoom(camera.id, 1), 'Zoom')}
                onPointerUp={() => ptzStop(camera.id)}
                onPointerLeave={() => ptzStop(camera.id)}
              >Zoom +</button>
              <button
                className={btnDefault}
                onPointerDown={() => cmd(() => ptzZoom(camera.id, -1), 'Zoom')}
                onPointerUp={() => ptzStop(camera.id)}
                onPointerLeave={() => ptzStop(camera.id)}
              >Zoom -</button>
              <button
                className={btnDefault}
                onClick={() => cmd(() => ptzAutoFocus(camera.id), 'Auto focus')}
              >Auto Focus</button>
            </div>
          </Section>

          {/* ── Lights ── */}
          <Section title="Lights">
            <div className="flex gap-1.5 flex-wrap">
              <button
                className={irOn ? btnActive : btnDefault}
                onClick={() => {
                  const next = !irOn
                  setIrOn(next)
                  cmd(() => setIrLight(camera.id, next), 'IR light')
                }}
              >IR {irOn ? 'ON' : 'OFF'}</button>

              <button
                className={whiteOn ? btnActive : btnDefault}
                onClick={() => {
                  const next = !whiteOn
                  setWhiteOn(next)
                  cmd(() => setWhiteLight(camera.id, next), 'White light')
                }}
              >White {whiteOn ? 'ON' : 'OFF'}</button>
            </div>

            <div className="flex gap-1.5 mt-2">
              <span className="text-[10px] text-gray-500 leading-6">Day/Night</span>
              {([
                [0, 'Auto'],
                [1, 'Day'],
                [2, 'Night'],
              ] as const).map(([mode, label]) => (
                <button
                  key={mode}
                  className={dayNight === mode ? btnActive : btnDefault}
                  onClick={() => {
                    setDayNight(mode)
                    cmd(() => setDayNightMode(camera.id, mode), 'Day/Night')
                  }}
                >{label}</button>
              ))}
            </div>
          </Section>

          {/* ── Wiper / Wash / Home ── */}
          <Section title="Utilities">
            <div className="flex gap-1.5 flex-wrap">
              <button
                className={wiperOn ? btnActive : btnDefault}
                onClick={() => {
                  const next = !wiperOn
                  setWiperOn(next)
                  cmd(() => next ? wiperOpen(camera.id) : wiperClose(camera.id), 'Wiper')
                }}
              >Wiper {wiperOn ? 'ON' : 'OFF'}</button>

              <button
                className={btnDefault}
                onClick={() => cmd(() => washOpen(camera.id), 'Wash')}
              >Wash</button>

              {hasPtz && (
                <button
                  className={btnDefault}
                  onClick={() => cmd(() => ptzSetHome(camera.id), 'Set home')}
                >Set Home</button>
              )}

              <button
                className={btnDefault}
                onClick={() => cmd(() => downloadSnapshot(camera.id, camera.name), 'Snapshot')}
              >Snapshot</button>
            </div>
          </Section>

          {/* ── Presets ── */}
          {hasPtz && (
            <Section title="Presets" collapsible open={showPresets} onToggle={() => setShowPresets(p => !p)}>
              {presets.length > 0 ? (
                <div className="space-y-1">
                  {presets.map((p) => (
                    <div key={p.id} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5">
                      <button
                        className="text-xs text-gray-700 hover:text-gray-900 transition-colors truncate flex-1 text-left"
                        onClick={() => cmd(() => ptzInvokePreset(camera.id, p.id), 'Preset')}
                      >
                        {p.name}
                      </button>
                      <button
                        className="text-xs text-gray-600 hover:text-error transition-colors ml-2"
                        onClick={() => {
                          cmd(() => ptzDeletePreset(camera.id, p.id), 'Delete preset')
                          setTimeout(loadPresets, 500)
                        }}
                      >&times;</button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[10px] text-gray-600">No presets saved</p>
              )}

              <div className="flex gap-1 mt-2">
                <input
                  type="text"
                  placeholder="Preset name..."
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddPreset()}
                  className="flex-1 bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs text-gray-900 placeholder-gray-400 outline-none focus:border-brand"
                />
                <button
                  className={btnDefault}
                  onClick={handleAddPreset}
                >Save</button>
              </div>
            </Section>
          )}

          {/* ── Capability badges ── */}
          {caps && caps.intelligences.length > 0 && onOpenSettings && (
            <Section title="AI Capabilities">
              <div className="flex flex-wrap gap-1">
                {caps.intelligences.includes(IntelligenceType.Perimeter) && <CapBadge label="Intrusion" />}
                {caps.intelligences.includes(IntelligenceType.TripWire) && <CapBadge label="Line Cross" />}
                {caps.intelligences.includes(IntelligenceType.EnterArea) && <CapBadge label="Enter Area" />}
                {caps.intelligences.includes(IntelligenceType.LeaveArea) && <CapBadge label="Leave Area" />}
                {caps.intelligences.includes(IntelligenceType.PeopleCounting) && <CapBadge label="People Count" />}
                {caps.intelligences.includes(IntelligenceType.SmartMotion) && <CapBadge label="Smart Motion" />}
                {caps.intelligences.includes(IntelligenceType.Loitering) && <CapBadge label="Loitering" />}
                {caps.intelligences.includes(IntelligenceType.ObjectLeft) && <CapBadge label="Object Left" />}
                {caps.intelligences.includes(IntelligenceType.FireSmokeDetect) && <CapBadge label="Fire/Smoke" />}
              </div>
            </Section>
          )}
        </div>
      )}
    </div>
  )
}

function CapBadge({ label }: { label: string }) {
  return (
    <span className="text-[10px] px-1.5 py-0.5 bg-brand/10 text-brand rounded font-medium">
      {label}
    </span>
  )
}

// ── Section helper ──

function Section({
  title,
  children,
  collapsible,
  open,
  onToggle,
}: {
  title: string
  children: React.ReactNode
  collapsible?: boolean
  open?: boolean
  onToggle?: () => void
}) {
  const isOpen = collapsible ? open : true

  return (
    <div>
      <button
        className={`text-[10px] uppercase tracking-wider text-gray-500 mb-2 block ${collapsible ? 'hover:text-gray-700 cursor-pointer' : 'cursor-default'}`}
        onClick={collapsible ? onToggle : undefined}
      >
        {collapsible && <span className="mr-1">{isOpen ? '▾' : '▸'}</span>}
        {title}
      </button>
      {isOpen && children}
    </div>
  )
}
